import React from 'react';
import { IHighPriorityEventItemProps } from '../types';
import { useEventItemPosition } from '../hooks/useEventItemPosition';
import { useHighPriorityMarkArea } from '../hooks/useHighPriorityMarkArea';
import { MarkArea, EventLine, EventHeader, EventBubble } from '../HighPriorityCommon';
import { formatRatio } from '../utils/eventBlockUtils';
import classNames from 'classnames';
import TextBubbles from '@home/components/TextBubbles';

export default function HighPriorityEventItem({
  myChart,
  xAxisValue,
  event,
  hide = true,
  format,
  backPositionInfo,
  halfBarWidth,
  maxTimeValue,
  showEventAxis,
  dateType,
  xAxisData,
  yAxisMin,
  onHighPriorityClick,
  hasAnySelected = false,
  anchorMode = 'start',
  trendText,
  bubbleContent,
  onBubbleVisibleChange,
  useBubbleText = false,
}: IHighPriorityEventItemProps) {
  const positionHook = useEventItemPosition({
    myChart,
    xAxisValue,
    format,
    dateType,
    xAxisData,
    backPositionInfo,
    yAxisMin,
    anchorMode,
    event,
  });

  const { eventBoxRef, eventLineRef } = positionHook;

  // 根据 ratio 判断趋势：负数=下降，正数或未定义=上升
  const isDown = event.ratio !== undefined ? event.ratio < 0 : false;

  // 使用公共 hook 处理 mark area 逻辑
  const {
    isSelected,
    isDimmed,
    markAreaShow,
    renderMarkArea,
    markAreaTimes,
    eventLineShow,
    markAreaWidth,
    handleMouseEnter,
    handleMouseLeave,
  } = useHighPriorityMarkArea({
    selected: event.selected,
    hasAnySelected,
    startTime: event.start_time,
    endTime: event.end_time,
    format,
    anchorMode,
    halfBarWidth,
    maxTimeValue,
    positionHook,
    myChart,
  });

  // 趋势方向
  const trend = isDown ? 'down' : 'up';

  return (
    <>
      {/* 公共组件：标记区域 */}
      <MarkArea
        show={markAreaShow}
        style={renderMarkArea}
        time={markAreaTimes}
        long={!showEventAxis}
        anchorMode={anchorMode}
        trend={trend}
        selected={isSelected}
        halfBarWidth={halfBarWidth}
        markAreaWidth={markAreaWidth}
      />
      <EventBubble
        bubbleContent={bubbleContent}
        onBubbleVisibleChange={onBubbleVisibleChange}
        event={event}
      >
        {/* 事件容器 */}
        <div ref={eventBoxRef} className="dbo-high-priority-wrapper">
          <div
            onMouseEnter={handleMouseEnter}
            onClick={() => onHighPriorityClick?.(event)}
            onMouseLeave={handleMouseLeave}
            className={classNames('dbo-high-priority-box', {
              '--down': isDown,
              '--up': !isDown,
              '--hide': hide,
              '--selected': isSelected,
              '--dimmed': isDimmed,
            })}
          >
            {/* 公共组件：头部 */}
            <EventHeader
              title={event.name || event.title || ''}
              description={event.description}
              ratio={event.ratio}
              trend={trend}
              tagName={event.tagName}
              tagNameZh={event.tagNameZh}
              tagNameEn={event.tagNameEn}
              trendText={trendText}
            />

            {/* 列表项（单个事件特有） */}
            {event.list && event.list.length > 0 && (
              <div className="dbo-hp-list">
                {(() => {
                  // 计算排序值：up 为正，down 为负
                  const getFactorValue = (idx: number) => {
                    const f = event.factors?.[idx];
                    if (!f?.contribution) return 0;
                    return f.trend === 'down'
                      ? -f.contribution
                      : f.contribution;
                  };
                  return event.list
                    .map((item: string, index: number) => ({ item, index }))
                    .sort(
                      (a, b) =>
                        getFactorValue(b.index) - getFactorValue(a.index),
                    )
                    .map(({ item, index }) => {
                      const factor = event.factors?.[index];
                      const isFactorDown = factor?.trend === 'down';
                      const ratioVal =
                        factor?.contribution !== null &&
                        factor?.contribution !== undefined
                          ? (isFactorDown
                              ? -factor.contribution
                              : factor.contribution) * 100
                          : null;
                      const factorRatio =
                        ratioVal !== null ? formatRatio(ratioVal) : null;
                      return (
                        <div
                          key={index}
                          className={classNames('dbo-hp-list-item', {
                            '--factor-down': isFactorDown,
                            '--factor-up': !isFactorDown,
                          })}
                        >
                          {useBubbleText ? (
                            <TextBubbles
                              className="dbo-hp-list-item-text"
                              text={item}
                            />
                          ) : (
                            <span className="dbo-hp-list-item-text">{item}</span>
                          )}
                          {factorRatio && (
                            <span
                              className={classNames('dbo-hp-list-item-ratio', {
                                '--down': isFactorDown,
                              })}
                            >
                              ({factorRatio.value})
                            </span>
                          )}
                        </div>
                      );
                    });
                })()}
              </div>
            )}
            <i className="dbo-hp-icon-logo"></i>
          </div>
        </div>
      </EventBubble>
      {/* 公共组件：连接线 */}
      <EventLine
        lineRef={eventLineRef}
        highlight={eventLineShow}
        trend={trend}
        selected={isSelected}
        dimmed={isDimmed}
      />
    </>
  );
}
